<template>
  <div class="py-12 lg:py-16">
    <div class="container mx-auto px-4 max-w-2xl">
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-3">{{ $t('feedback.title') }}</h1>
        <p class="text-gray-600 leading-relaxed">
          {{ $t('feedback.intro') }}
        </p>
      </div>

      <!--div
        v-if="showLinkedPoiBanner"
        class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 mb-6 text-center sm:text-left"
        role="status"
      >
        <p class="text-sm font-semibold text-gray-900">{{ linkedPoiDisplayName }}</p>
      </div-->

      <div v-if="submitted" class="rounded-xl border border-green-200 bg-green-50 p-5">
        <div class="font-semibold text-green-900">{{ $t('feedback.successTitle') }}</div>
        <div class="text-sm text-green-800 mt-1">
          {{ $t('feedback.successBody') }}
        </div>
        <button
          type="button"
          class="mt-4 inline-flex items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-green-900 border border-green-200 hover:bg-green-100 transition"
          @click="resetForm"
        >
          {{ $t('feedback.submitAnother') }}
        </button>
      </div>

      <form v-else class="space-y-4" @submit.prevent="submit">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-800 mb-1">{{ $t('feedback.typeLabel') }}</label>
            <select
              v-model="form.type"
              class="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="suggest_place">{{ $t('feedback.typeSuggestPlace') }}</option>
              <option value="report_inaccuracy">{{ $t('feedback.typeReportInaccuracy') }}</option>
              <option value="general">{{ $t('feedback.typeGeneral') }}</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-800 mb-1">{{ $t('feedback.emailOptional') }}</label>
            <input
              v-model.trim="form.email"
              type="email"
              autocomplete="email"
              :placeholder="$t('feedback.emailPlaceholder')"
              class="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-800 mb-1">{{ $t('feedback.subjectOptional') }}</label>
          <input
            v-model.trim="form.subject"
            type="text"
            maxlength="200"
            :placeholder="$t('feedback.subjectPlaceholder')"
            class="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <div class="mb-1 flex items-baseline justify-between gap-3">
            <label class="block text-sm font-medium text-gray-800">{{ $t('feedback.messageLabel') }}</label>
            <div class="text-xs text-gray-500 whitespace-nowrap">
              {{ $t('feedback.charCount', { n: form.message.length }) }}
            </div>
          </div>
          <textarea
            v-model.trim="form.message"
            rows="7"
            maxlength="5000"
            required
            :placeholder="$t('feedback.messagePlaceholder')"
            class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          ></textarea>
        </div>

        <div v-if="apiError" class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {{ apiError }}
        </div>

        <div class="flex justify-end">
          <button
            type="submit"
            class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
            :disabled="isLoading"
          >
            <svg
              v-if="!isLoading"
              class="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 10.5l19-7-7 19-2.5-7.5L3 10.5z"
              />
            </svg>
            <svg
              v-else
              class="h-4 w-4 shrink-0 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span v-if="!isLoading">{{ $t('feedback.send') }}</span>
            <span v-else>{{ $t('feedback.sending') }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useApi } from '../composables/useApi';

const route = useRoute();
const { t } = useI18n();
const { request, isLoading } = useApi();

const submitted = ref(false);
const apiError = ref(null);

/** Query from Explore (/location) feedback link — see LocationView `localeFeedbackPath`. */
const fromLocation = computed(() => {
  const source = String(route.query.source || '');
  return source === 'location';
});

const linkedPoiName = computed(() => String(route.query.poiName ?? '').trim());
const linkedPoiId = computed(() => String(route.query.poiId ?? '').trim());

/** True when user had a POI selected on the location page (id and/or name in query). */
const hasLinkedLocationPoi = computed(
  () => fromLocation.value && !!(linkedPoiName.value || linkedPoiId.value)
);

/** Show name-only banner when arriving from Explore with a selected POI. */
//const showLinkedPoiBanner = computed(() => hasLinkedLocation2Poi.value);

/*const linkedPoiDisplayName = computed(() => {
  if (linkedPoiName.value) return linkedPoiName.value;
  if (linkedPoiId.value) return `#${linkedPoiId.value}`;
  return '';
});*/

function applyQueryDefaults() {
  if (!fromLocation.value) {
    form.type = 'general';
    form.subject = '';
    return;
  }
  if (hasLinkedLocationPoi.value) {
    form.type = 'report_inaccuracy';
    form.subject = linkedPoiName.value || '';
    form.context = {
      poiId: linkedPoiId.value,
      poiName: linkedPoiName.value
    };
    return;
  }
  form.type = 'suggest_place';
  form.subject = '';
}

const form = reactive({
  type: 'general',
  subject: '',
  message: '',
  email: ''
});

onMounted(() => {
  applyQueryDefaults();
});

watch(
  () => [route.query.source, route.query.poiId, route.query.poiName],
  () => {
    if (!submitted.value) {
      applyQueryDefaults();
    }
  }
);

async function submit() {
  apiError.value = null;
  try {
    const payload = {
      type: form.type,
      subject: form.subject || null,
      message: form.message,
      email: form.email || null,
    };
    if (hasLinkedLocationPoi.value) {
      payload.context = {
        poiId: linkedPoiId.value,
        poiName: linkedPoiName.value
      };
    }
    //console.log('payload', payload);
    await request('/feedback', { method: 'POST', body: JSON.stringify(payload) });
    submitted.value = true;
  } catch (e) {
    apiError.value = e?.message || t('feedback.errorSend');
  }
}

function resetForm() {
  submitted.value = false;
  apiError.value = null;
  form.message = '';
  form.email = '';
  applyQueryDefaults();
}
</script>
